import React from 'react';
import { Link } from 'react-router-dom';
import { FormattedNumber } from 'react-intl';
import dayjs from 'dayjs';
import styled from 'styled-components';

import Body1 from '@joinforma/ui/components/Body1';
import Button from '@joinforma/ui/components/Button';
import Display2 from '@joinforma/ui/components/Display2';
import Headline5 from '@joinforma/ui/components/Headline5';
import { Color } from '@joinforma/ui/constants/themes';

import { StoreRoutes } from 'utils/routeGenerator';
import { logEvent } from 'utils/amplitude';
import { logRudderstackEvent } from 'utils/rudderstack';

import { ScreenWidth } from '@joinforma/ui/constants/breakpoints';
import { EVENTS } from 'constants/events';
import { EXPIRING_FUNDS_EVENT_MAP_V2 } from 'constants/featureFlag';

import WalletIcon from 'components/common/WalletIcon';

import { ButtonSize, ButtonVariant } from '@joinforma/ui/constants/properties';

const BannerCardsContainer = styled.div`
  width: 343px;

  @media (min-width: ${ScreenWidth.MOBILE_LANDSCAPE}) {
    width: 880px;
    height: auto;
    margin-bottom: 32px;
  }
`;

const TopInfoContainer = styled.div`
  margin-bottom: 32px;
`;

const Title = styled(Headline5)`
  margin-bottom: 8px;
`;
const Text = styled(Body1)`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-bottom: 32px;

  @media (min-width: ${ScreenWidth.MOBILE_LANDSCAPE}) {
    flex-direction: row;
  }
`;

interface CardContainerProps {
  index?: number;
  count?: number;
}

const CardContainer = styled.div<CardContainerProps>`
  padding: 24px;
  width: 100%;
  border-radius: 16px;
  background-color: ${({ index }) =>
    index === 0
      ? Color.CYAN_200
      : index === 1
      ? Color.YELLOW_200
      : Color.GREEN_200};

  @media (min-width: ${ScreenWidth.MOBILE_LANDSCAPE}) {
    padding: 48px;
    flex-direction: ${({ count }) => (count === 2 ? 'row' : 'column')};
    ${({ count }) => (count === 2 ? 'justify-content: space-between' : '')};
  }
`;

const CardSubContainer = styled.div<CardContainerProps>`
  display: flex;
  justify-content: space-between;

  @media (min-width: ${ScreenWidth.MOBILE_LANDSCAPE}) {
    ${({ count }) => (count === 3 ? 'flex-direction: column' : '')};
  }
`;

const CardLeftContainer = styled.div`
  display: flex;
  justify-content: center;
`;
const StyledWalletIcon = styled(WalletIcon)<CardContainerProps>`
  width: 120px;
  height: 120px;
  margin-bottom: 32px;
`;

const CardRightContainer = styled.div<CardContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;

  @media (min-width: ${ScreenWidth.MOBILE_LANDSCAPE}) {
    align-items: ${({ count }) => (count === 2 ? 'flex-end' : 'center')};
  }
`;
const AmountDisplay = styled(Display2)``;
const WalletName = styled(Body1)`
  display: block;
  text-align: right;
`;

const FormattedLink = styled(Link)``;
const MobileFormattedLink = styled(Link)`
  @media (min-width: ${ScreenWidth.MOBILE_LANDSCAPE}) {
    display: none;
  }
`;

const ShopStore = styled(Button).attrs({
  variant: ButtonVariant.TERTIARY,
  size: ButtonSize.SMALL,
})`
  display: none;

  @media (min-width: ${ScreenWidth.MOBILE_LANDSCAPE}) {
    display: flex;
    width: 138px;
    height: 48px;
    padding: 0px;
  }
`;

const MobileShopStore = styled(Button).attrs({
  variant: ButtonVariant.TERTIARY,
  size: ButtonSize.SMALL,
})`
  display: flex;
  width: 298px;
  height: 48px;
  padding: 0px;

  @media (min-width: ${ScreenWidth.MOBILE_LANDSCAPE}) {
    display: none;
  }
`;

const ActiveAccounts = styled(Headline5)`
  margin-bottom: 32px;

  @media (min-width: ${ScreenWidth.MOBILE_LANDSCAPE}) {
    display: none;
  }
`;

const ExpiringFundsCards = ({
  wallets_expiring,
  currency,
  days_left,
  total,
  variation,
}: {
  wallets_expiring: Array<{
    id: string;
    wallet_type_id: string;
    amount: number;
    next_credit_date?: string;
    company_wallet_configuration: { wallet_name: string };
  }>;
  currency: string;
  days_left: number;
  total: number;
  variation: string;
}) => {
  const handleClick = (variation: string) => {
    const attributes = { variation: EXPIRING_FUNDS_EVENT_MAP_V2[variation] };
    logEvent(EVENTS.ExpiringFundsExperimentBannerClicked, attributes);
    logRudderstackEvent(
      EVENTS.ExpiringFundsExperimentBannerClicked,
      attributes
    );
  };
  return (
    <BannerCardsContainer>
      <TopInfoContainer>
        <Title>{`Expiring balances in ${days_left} days`}</Title>
        <Text>
          {`You could lose out on `}
          <FormattedNumber
            value={Math.floor(total)}
            currency={currency}
            {...{ style: 'currency', maximumFractionDigits: 0 }}
          />{' '}
          {`if you don't spend or claim before ${dayjs(
            wallets_expiring[0].next_credit_date
          ).format('MMMM DD')}`}
        </Text>
      </TopInfoContainer>
      <CardsContainer>
        {wallets_expiring.map(
          (
            { id, wallet_type_id, amount, company_wallet_configuration },
            index
          ) => (
            <CardContainer
              index={index}
              count={wallets_expiring.length}
              key={id}
            >
              <CardSubContainer count={wallets_expiring.length}>
                <CardLeftContainer>
                  <StyledWalletIcon
                    wallet_type_id={wallet_type_id}
                    count={wallets_expiring.length}
                  />
                </CardLeftContainer>
                <CardRightContainer count={wallets_expiring.length}>
                  <AmountDisplay>
                    <FormattedNumber
                      value={Math.floor(amount)}
                      currency={currency}
                      {...{ style: 'currency', maximumFractionDigits: 0 }}
                    />
                  </AmountDisplay>
                  <WalletName>
                    {company_wallet_configuration.wallet_name}
                  </WalletName>
                  <FormattedLink
                    to={
                      variation.startsWith('control')
                        ? StoreRoutes.collectionNoCost()
                        : StoreRoutes.accountBrowser({ walletId: id })
                    }
                    onClick={() => handleClick(variation)}
                  >
                    <ShopStore>Explore store</ShopStore>
                  </FormattedLink>
                </CardRightContainer>
              </CardSubContainer>
              <MobileFormattedLink
                to={
                  variation.startsWith('control')
                    ? StoreRoutes.collectionNoCost()
                    : StoreRoutes.accountBrowser({ walletId: id })
                }
                onClick={() => handleClick(variation)}
              >
                <MobileShopStore>Explore store</MobileShopStore>
              </MobileFormattedLink>
            </CardContainer>
          )
        )}
      </CardsContainer>
      <ActiveAccounts>Active Accounts</ActiveAccounts>
    </BannerCardsContainer>
  );
};

export default ExpiringFundsCards;
